type RuntimeEnv = Partial<Record<"VITE_API_URL", string>>;

const runtimeEnv =
  typeof window !== "undefined" && (window as Window & { __ENV__?: RuntimeEnv }).__ENV__
    ? (window as Window & { __ENV__?: RuntimeEnv }).__ENV__ ?? {}
    : {};

const normalizeEnvValue = (value?: string | null) => {
  if (typeof value !== "string") return null;
  const normalized = value.trim();
  return normalized.length ? normalized : null;
};

export const API_URL =
  normalizeEnvValue(runtimeEnv.VITE_API_URL) ??
  normalizeEnvValue(import.meta.env.VITE_API_URL) ??
  "http://localhost:3000";

const getStored = (key: string) => {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(key);
};

export const getAuthToken = () => getStored("omni:token");

export const setAuthSession = (token: string, tenantId?: string) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem("omni:token", token);
  const resolvedTenantId = tenantId ?? decodeJwtTenantId(token);
  if (resolvedTenantId) {
    window.localStorage.setItem("omni:tenantId", resolvedTenantId);
  } else {
    window.localStorage.removeItem("omni:tenantId");
  }
  const roles = decodeJwtRoles(token);
  if (roles.length) {
    window.localStorage.setItem("omni:roles", roles.join(","));
  } else {
    window.localStorage.removeItem("omni:roles");
  }
};

export const clearAuthSession = () => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem("omni:token");
  window.localStorage.removeItem("omni:tenantId");
  window.localStorage.removeItem("omni:roles");
  window.localStorage.removeItem("omni:customRoles");
  window.localStorage.removeItem("omni:activeRoleId");
  window.localStorage.removeItem("omni:brandColor");
};

const buildHeaders = (headers?: HeadersInit) => {
  const token = getStored("omni:token");
  const tenantId = getStored("omni:tenantId");
  const decodedRoles = token ? decodeJwtRoles(token) : [];
  const persistedRoles = getStored("omni:roles");
  const roles = decodedRoles.length
    ? decodedRoles.join(",")
    : persistedRoles
    ? persistedRoles
    : "";

  return {
    "Content-Type": "application/json",
    ...(tenantId ? { "x-tenant-id": tenantId } : {}),
    ...(roles ? { "x-user-roles": roles } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...headers,
  };
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const safeJson = async (response: Response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

const fetchWithRetry = async (url: string, options: RequestInit, retryOnce: boolean) => {
  try {
    return await fetch(url, options);
  } catch (error) {
    if (retryOnce) {
      await sleep(600);
      return await fetch(url, options);
    }
    throw error;
  }
};

export const apiRequest = async <T>(path: string, options: RequestInit = {}) => {
  const url = `${API_URL}${path}`;
  const method = (options.method ?? "GET").toUpperCase();
  let response: Response;
  try {
    response = await fetchWithRetry(
      url,
      {
        ...options,
        headers: buildHeaders(options.headers),
      },
      method === "GET"
    );
  } catch (error) {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      if (typeof window !== "undefined") {
        const handleOnline = () => {
          window.removeEventListener("online", handleOnline);
          window.location.reload();
        };
        window.addEventListener("online", handleOnline);
      }
      throw new Error("Sem conexão com a internet. Reconectando...");
    }
    throw new Error("Conexão perdida. Recarregue a página e tente novamente.");
  }

  if (response.status === 401) {
    clearAuthSession();
    if (typeof window !== "undefined" && window.location.pathname !== "/login") {
      const from = `${window.location.pathname}${window.location.search}`;
      window.location.assign(`/login?from=${encodeURIComponent(from)}`);
    }
  }

  if (!response.ok) {
    const errorBody = await safeJson(response);
    if (typeof errorBody === "string") {
      throw new Error(errorBody);
    }
    if (errorBody && typeof errorBody === "object") {
      const rawMessage = (errorBody as { message?: unknown; error?: unknown }).message;
      const message =
        typeof rawMessage === "string"
          ? rawMessage
          : Array.isArray(rawMessage)
          ? rawMessage.filter((item) => typeof item === "string").join(" ")
          : typeof (errorBody as { error?: unknown }).error === "string"
          ? (errorBody as { error: string }).error
          : "";
      throw new Error(message || response.statusText);
    }
    throw new Error(response.statusText);
  }

  return (await safeJson(response)) as T;
};

// Backend/provider errors (e.g. Meta Graph API text) can be long or contain
// raw payload-like content; cap length and reject anything that doesn't look
// like a short human sentence before showing it to the agent.
export const getUserFacingErrorMessage = (error: unknown, fallback: string, maxLength = 200) => {
  const message = error instanceof Error ? error.message.trim() : "";
  if (!message || message.length > maxLength || /[{}[\]<>]/.test(message)) {
    return fallback;
  }
  return message;
};

export const apiGet = async <T>(path: string) => apiRequest<T>(path, { method: "GET" });

export const apiPost = async <T>(path: string, body: unknown) =>
  apiRequest<T>(path, {
    method: "POST",
    body: JSON.stringify(body),
  });

export const apiPatch = async <T>(path: string, body: unknown) =>
  apiRequest<T>(path, {
    method: "PATCH",
    body: JSON.stringify(body),
  });

export const apiDelete = async <T>(path: string) =>
  apiRequest<T>(path, {
    method: "DELETE",
  });

export const apiUpload = async <T>(path: string, file: File, fields?: Record<string, string>) => {
  const form = new FormData();
  form.append("file", file);
  if (fields) {
    Object.entries(fields).forEach(([key, value]) => form.append(key, value));
  }
  const headers = buildHeaders();
  delete (headers as Record<string, string>)["Content-Type"];

  const response = await fetch(`${API_URL}${path}`, {
    method: "POST",
    headers,
    body: form,
  });

  if (!response.ok) {
    const errorBody = await safeJson(response);
    throw new Error(typeof errorBody === "string" ? errorBody : response.statusText);
  }

  return (await safeJson(response)) as T;
};

export const getAuthRoles = () => {
  const token = getStored("omni:token");
  return token ? decodeJwtRoles(token) : [];
};

export const getAuthTenantId = () => {
  const token = getStored("omni:token");
  return token ? decodeJwtTenantId(token) : null;
};

const decodeJwtRoles = (token: string) => {
  const payload = decodeJwtPayload(token) as { roles?: unknown } | null;
  const roles = payload?.roles;
  return Array.isArray(roles) ? roles.filter((role): role is string => typeof role === "string") : [];
};

const decodeJwtTenantId = (token: string) => {
  const payload = decodeJwtPayload(token) as { tenantId?: unknown } | null;
  const tenantId = payload?.tenantId;
  return typeof tenantId === "string" && tenantId.trim().length ? tenantId : null;
};

const decodeJwtPayload = (token: string) => {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    if (typeof window === "undefined" || typeof window.atob !== "function") return null;
    const json = window.atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
};
